// ---------------------------------------------------------------------------
// JobRemindersReview
//
// One list for one kind of job reminder (utils/jobReminders.js):
//   kind="proposalJob" new jobs from approved proposals: set the date, techs
//                     and job description right in the row
//   kind="dupWO"      one WO number at 2+ properties: give one property a new
//                     number on every record (utils/woNumbers.js)
//   kind="noTech"     jobs with nobody on Tech Assign
//   kind="clockedIn"  clock-ins that aren't on any job that day
//   kind="subPay"     subs on a job with no pay set (Admin only)
//
//   mode="modal"  -> the popup on Home (opens once a day until handled)
//   mode="inline" -> Job History -> Reminders (every job)
//
// Dismiss is permanent (jobReminderDismissals) and asks why. "Later" hides a
// row until tomorrow on this browser (modal only). Looks and works like
// Missed Jobs, using the same styles.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { getAuth } from "firebase/auth";
import { blockIfViewingAs } from "../utils/viewAs";
import {
  REMINDER_KINDS,
  assignPersonForDay,
  assignTechsToJob,
  dismissReminder,
  saveProposalJobSetup,
  techList,
} from "../utils/jobReminders";
import SubPayPanel from "./SubPayPanel";
import { renderFormattedText } from "./FormattedText";
import {
  applyWORenumber,
  getNextFreeSchedulingNumber,
  getNextFreeWorkOrder,
  planWORenumber,
  proposalPrefixOf,
} from "../utils/woNumbers";
import { formattedToOneLine } from "../utils/formattedText";
import { HowToButton } from "./HowToGuide";
import { REMINDER_TOPIC } from "../utils/howToTopics";
import "./MissedJobsReview.css";
import "./JobRemindersReview.css";

const fmtDay = (key) => (key ? moment(key, "YYYY-MM-DD").format("ddd, MMM D, YYYY") : "No date");
const fmtShort = (key) => (key ? moment(key, "YYYY-MM-DD").format("MMM D, YYYY") : "No date");
const fmtTime = (d) => (d ? moment(d).format("h:mm A") : "");
const fmtHours = (mins) => `${Math.floor((mins || 0) / 60)}h ${String(Math.round((mins || 0) % 60)).padStart(2, "0")}m`;

const AGE_FILTERS = [
  { id: "all", label: "All", test: () => true },
  { id: "upcoming", label: "Today & ahead", test: (r) => r.day && r.day >= moment().format("YYYY-MM-DD") },
  { id: "week", label: "Last 7 days", test: (r) => r.day && r.day < moment().format("YYYY-MM-DD") && r.day >= moment().subtract(7, "days").format("YYYY-MM-DD") },
  { id: "older", label: "Older", test: (r) => r.day && r.day < moment().subtract(7, "days").format("YYYY-MM-DD") },
  { id: "nodate", label: "No date", test: (r) => !r.day },
];

const KIND_WORD = {
  jobs: "job",
  addItem: "job",
  approvedInvoices: "invoice",
  proposals: "proposal",
  deposits: "payment",
  jobChecklist: "checklist",
  jobContracts: "Job Money",
  bulk_material_expenses: "material receipt",
  bulk_income: "income entry",
  monthly_expenses: "expense",
  subcontractorPay: "sub pay",
  subcontractorPayments: "sub payment",
  bankStatementEntries: "bank entry",
  recurring_job_occurrences: "recurring visit",
  techCompletionRequests: "tech completion",
  transactions: "Financials entry",
};

/* dupWO: give ONE property a new WO on every record it has. */
const DupWOFix = ({ row, site, onDone, onCancel }) => {
  const [newWO, setNewWO] = useState("");
  const [plan, setPlan] = useState(null);
  const [includeUnlinked, setIncludeUnlinked] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const run = async (label, fn) => {
    setBusy(label);
    setError("");
    try {
      await fn();
    } catch (err) {
      console.error(err);
      setError(err?.message || String(err));
    } finally {
      setBusy("");
    }
  };

  const suggest = () =>
    run("suggest", async () => {
      const prefix = proposalPrefixOf(row.wo);
      setNewWO(prefix ? await getNextFreeWorkOrder(prefix) : String(await getNextFreeSchedulingNumber()));
      setPlan(null);
    });

  const preview = (unlinked = includeUnlinked) =>
    run("preview", async () => {
      setPlan(
        await planWORenumber({ oldWO: row.wo, newWO, siteName: site.siteName, includeBranches: true, includeUnlinked: unlinked })
      );
    });

  const apply = () =>
    run("apply", async () => {
      if (blockIfViewingAs()) return;
      if (!plan) return;
      if (plan.newTaken) throw new Error(`${plan.newWO} is already used. Pick another number.`);
      const ok = window.confirm(
        `Change ${plan.oldWO} to ${plan.newWO} on ${plan.changes.length} record(s) at ${site.siteName}?\n\n` +
          `${plan.othersKeep ? `${plan.keptSites.join(", ") || "The other property"} keeps ${plan.oldWO}.\n` : ""}` +
          "Undo is in WO Totals Repair → Recent WO changes."
      );
      if (!ok) return;
      const res = await applyWORenumber(plan);
      onDone && onDone(`${site.siteName}: ${plan.oldWO} → ${plan.newWO} on ${res.changed} record(s).`);
    });

  return (
    <div className="jr-setup" onClick={(e) => e.stopPropagation()}>
      <div className="mj-step-text" style={{ marginTop: 0 }}>
        <b>{site.siteName}</b> gets a new number on its jobs (and -2, -3 branches), invoices, payments, proposal, checklist,
        Job Money and totals. {row.sites.filter((x) => x !== site).map((x) => x.siteName).join(", ")} keeps {row.wo}.
      </div>
      <div className="jr-setup-grid">
        <label className="mj-field">
          <span>New WO</span>
          <input type="text" value={newWO} onChange={(e) => { setNewWO(e.target.value); setPlan(null); }} placeholder="PJMC-10052" />
        </label>
        <div className="mj-field" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="mj-btn" onClick={suggest} disabled={Boolean(busy)}>
            {busy === "suggest" ? "Finding…" : "Use next free number"}
          </button>
        </div>
      </div>

      {plan && (
        <div className="jr-dup-plan">
          {plan.newTaken && <div className="mj-problem">{plan.newWO} is already used by another record - pick another number.</div>}
          <div>
            <b>{plan.changes.length}</b> record(s) at {site.siteName} will change
            {plan.totals.length ? `, plus ${plan.totals.length} total(s)` : ""}:
          </div>
          <ul>
            {Object.entries(
              plan.changes.reduce((m, c) => ({ ...m, [c.collection]: (m[c.collection] || 0) + 1 }), {})
            ).map(([col, n]) => (
              <li key={col}>
                {n} × {KIND_WORD[col] || col}
              </li>
            ))}
          </ul>
          {plan.totalsKept?.length > 0 && (
            <div className="mj-history">
              The job total on {plan.totalsKept.map((t) => t.from).join(", ")} stays with {plan.keptSites.join(", ") || "the other property"}.
            </div>
          )}
          {plan.unknownSite.length > 0 && (
            <div className="mj-history">
              {plan.unknownSite.length} record(s) with this WO have no property and aren't tied to this property's job or invoice, so
              they keep {plan.oldWO}.{" "}
              <button
                type="button"
                className="mj-link"
                onClick={() => {
                  setIncludeUnlinked(true);
                  preview(true);
                }}
              >
                They belong to {site.siteName} - include them
              </button>
            </div>
          )}
        </div>
      )}

      {error && <div className="mj-msg mj-msg--error">{error}</div>}
      <div className="mj-step-foot">
        <button type="button" className="mj-link" onClick={onCancel} disabled={Boolean(busy)}>
          Cancel
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="mj-btn" onClick={() => preview()} disabled={!newWO.trim() || Boolean(busy)}>
            {busy === "preview" ? "Checking…" : "Preview"}
          </button>
          <button type="button" className="mj-go" onClick={apply} disabled={!plan || plan.newTaken || Boolean(busy)}>
            {busy === "apply" ? "Changing…" : "Change everywhere"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* proposalJob: date, techs and description for one new job, in its row. */
const ProposalJobSetup = ({ row, staffNames = [], staffName = "", onSaved, onCancel }) => {
  const job = row.job || {};
  const [start, setStart] = useState(String(job.startDate || "").slice(0, 10));
  const [end, setEnd] = useState(String(job.endDate || "").slice(0, 10));
  const [techs, setTechs] = useState(() => techList(job.techAssign));
  const [desc, setDesc] = useState(job.jobDescription || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const problem = !start ? "Pick a start date." : !techs.length ? "Add at least one tech." : "";

  const save = async () => {
    if (problem || saving) return;
    if (blockIfViewingAs()) return;
    setSaving(true);
    setError("");
    try {
      const u = getAuth().currentUser;
      const patch = await saveProposalJobSetup(job, {
        startDate: start,
        endDate: end || start,
        techAssign: techs,
        jobDescription: desc,
        by: staffName || u?.displayName || u?.email || "",
      });
      onSaved && onSaved(patch);
    } catch (err) {
      console.error("Proposal job setup failed:", err);
      setError(err?.message || "Could not save.");
      setSaving(false);
    }
  };

  return (
    <div className="jr-setup" onClick={(e) => e.stopPropagation()}>
      <div className="jr-setup-grid">
        <label className="mj-field">
          <span>Start date</span>
          <input
            type="date"
            value={start}
            onChange={(e) => {
              setStart(e.target.value);
              if (!end || end < e.target.value) setEnd(e.target.value);
            }}
          />
        </label>
        <label className="mj-field">
          <span>End date</span>
          <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} />
        </label>
        <label className="mj-field mj-field--wide">
          <span>Techs</span>
          <select
            value=""
            onChange={(e) => {
              const v = e.target.value;
              if (v) setTechs((p) => (p.includes(v) ? p : [...p, v]));
            }}
          >
            <option value="">Add a tech…</option>
            {staffNames.filter((n) => !techs.includes(n)).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <div className="jr-picked">
            {techs.map((n) => (
              <button key={n} type="button" className="jr-pill" onClick={() => setTechs((p) => p.filter((x) => x !== n))}>
                {n} ✕
              </button>
            ))}
          </div>
        </label>
        <label className="mj-field mj-field--wide">
          <span>Job description (what the crew sees)</span>
          <textarea rows={6} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Scope of work for the crew…" />
          <small className="jr-setup-hint">
            ((b)) bold · ((r)) regular · ((-)) bullet - the preview shows what the crew sees.
          </small>
        </label>
        {desc.trim() && (
          <div className="mj-field mj-field--wide">
            <span>Preview</span>
            <div className="jr-setup-preview">{renderFormattedText(desc, { fallback: "—" })}</div>
          </div>
        )}
      </div>
      {error && <div className="mj-msg mj-msg--error">{error}</div>}
      <div className="mj-step-foot">
        {problem ? <span className="mj-problem">{problem}</span> : <span />}
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="mj-link" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="mj-go" disabled={Boolean(problem) || saving} onClick={save}>
            {saving ? "Saving…" : "Save & schedule"}
          </button>
        </div>
      </div>
    </div>
  );
};

const JobRemindersReview = ({
  kind,
  rows = [],
  mode = "modal",
  open = true,
  onClose,
  onRemindLater,
  onSnoozeKeys,
  snoozedKeys = [],
  staffNames = [],
  staffName = "",
  userRole = "",
  onJobPatch,
  onDismissed,
  onOpenJob,
  onRenumbered,
  loading = false,
  scopeNote = "",
}) => {
  const def = REMINDER_KINDS[kind];
  const isModal = mode === "modal";

  const [search, setSearch] = useState("");
  const [age, setAge] = useState("all");
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [step, setStep] = useState(null); // "assign" | "dismiss"
  const [pickNames, setPickNames] = useState([]);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const [rowPick, setRowPick] = useState({}); // clockedIn: key -> jobId
  const [payOpen, setPayOpen] = useState(""); // subPay: key with the panel open
  const [setupOpen, setSetupOpen] = useState(""); // proposalJob: key with the setup open
  const [setupDone, setSetupDone] = useState(() => new Set()); // saved this session
  const [dupOpen, setDupOpen] = useState(""); // dupWO: `${key}|${site}` with the fix open

  const snoozedSet = useMemo(() => new Set(snoozedKeys), [snoozedKeys]);

  useEffect(() => {
    setSelected((prev) => {
      const live = new Set(rows.map((r) => r.key));
      const next = new Set([...prev].filter((k) => live.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const visibleRows = useMemo(() => {
    const ageTest = AGE_FILTERS.find((f) => f.id === age)?.test || (() => true);
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (isModal && !showSnoozed && snoozedSet.has(r.key)) return false;
      if (!ageTest(r)) return false;
      if (!term) return true;
      return [r.wo, r.siteName, r.city, r.serviceType, r.name, r.subName, r.label, r.job?.clientWorkOrder]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [rows, age, search, isModal, showSnoozed, snoozedSet]);

  const ageCounts = useMemo(() => {
    const c = {};
    AGE_FILTERS.forEach((f) => {
      c[f.id] = rows.filter((r) => f.test(r) && !(isModal && !showSnoozed && snoozedSet.has(r.key))).length;
    });
    return c;
  }, [rows, isModal, showSnoozed, snoozedSet]);

  const snoozedCount = isModal ? rows.filter((r) => snoozedSet.has(r.key)).length : 0;
  const selectedRows = rows.filter((r) => selected.has(r.key));
  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((r) => selected.has(r.key));

  const toggleRow = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleRows.forEach((r) => next.delete(r.key));
      else visibleRows.forEach((r) => next.add(r.key));
      return next;
    });

  const who = () => {
    const u = getAuth().currentUser;
    return { by: staffName || u?.displayName || u?.email || "", byUid: u?.uid || "" };
  };

  const openStep = (s) => {
    setStep(s);
    setReason("");
    setNote("");
    setPickNames([]);
    setMessage(null);
  };

  const stepProblem = (() => {
    if (!step) return "";
    if (!selectedRows.length) return "Select at least one row.";
    if (step === "assign" && !pickNames.length) return "Pick at least one tech.";
    if (step === "dismiss" && !reason) return "Pick why this doesn't need fixing.";
    if (step === "dismiss" && reason === "Other" && !note.trim()) return "Add a short note for “Other”.";
    return "";
  })();

  const runStep = async () => {
    if (stepProblem || busy) return;
    if (blockIfViewingAs()) return;
    const targets = selectedRows;
    const failures = [];
    let done = 0;
    setBusy({ done: 0, total: targets.length });
    for (const row of targets) {
      try {
        if (step === "assign") {
          const next = await assignTechsToJob(row.jobId, pickNames);
          if (onJobPatch) onJobPatch(row.jobId, { techAssign: next });
        } else {
          const saved = await dismissReminder(row, { reason, note, ...who() });
          if (onDismissed) onDismissed(saved);
        }
        done += 1;
      } catch (err) {
        console.error("Reminder action failed:", row.key, err);
        failures.push(`${row.label}: ${err?.message || err}`);
      }
      setBusy({ done: done + failures.length, total: targets.length });
    }
    setBusy(null);
    setSelected(new Set());
    if (failures.length) {
      setMessage({ kind: "error", text: `${done} saved. ${failures.length} did not save: ${failures.slice(0, 3).join("; ")}` });
    } else {
      setStep(null);
      setMessage({ kind: "ok", text: `${step === "assign" ? "Assigned" : "Dismissed"}: ${done} done.` });
    }
  };

  // clockedIn: put this person on the chosen job for that day.
  const assignClockIn = async (row) => {
    const jobId = rowPick[row.key] ?? row.suggestedJobId;
    const option = row.options.find((o) => o.id === jobId);
    if (!option) return;
    if (blockIfViewingAs()) return;
    setBusy({ done: 0, total: 1, key: row.key });
    try {
      const { id, patch } = await assignPersonForDay(option.job, row.name, row.day);
      if (onJobPatch) onJobPatch(id, patch);
      setMessage({ kind: "ok", text: `${row.name} added to ${option.label} on ${fmtShort(row.day)}.` });
    } catch (err) {
      console.error(err);
      setMessage({ kind: "error", text: `Could not assign: ${err?.message || err}` });
    } finally {
      setBusy(null);
    }
  };

  const snoozeSelected = () => {
    if (!selectedRows.length || !onSnoozeKeys) return;
    onSnoozeKeys(selectedRows.map((r) => r.key));
    setSelected(new Set());
    setMessage({ kind: "ok", text: `${selectedRows.length} will come back tomorrow.` });
  };

  if (!def) return null;
  if (isModal && !open) return null;

  const empty = !rows.length;

  const body = (
    <div className={`mj jr jr--${kind} ${isModal ? "mj--modal" : "mj--inline"}`} onClick={(e) => e.stopPropagation()}>
      <div className="mj-head">
        <div>
          <h2 className="mj-title">
            <span aria-hidden="true">{def.icon}</span> {def.title}
            <span className="mj-count jr-count">{rows.length}</span>
          </h2>
          <p className="mj-sub">
            {loading
              ? "Checking…"
              : empty
              ? "Nothing to fix here."
              : kind === "dupWO"
              ? "One WO number is on records at more than one property, so their totals, invoices and payments mix together. Give the property that should change a new number - it changes on every record at that property."
              : kind === "proposalJob"
              ? "These jobs were just created from approved proposals (or a paid deposit). Give each one a date and techs and check the job description the crew will see."
              : kind === "noTech"
              ? "Nobody is on Tech Assign. Add the techs who did (or will do) the work, or dismiss jobs that never need a tech."
              : kind === "clockedIn"
              ? "These people clocked in but weren't on any job that day. Pick the job they worked on, or dismiss it (shop work, training…)."
              : "A Sub Contractor is on these jobs but no pay is set. Set Daily or Project pay, or dismiss if it was paid another way."}
          </p>
          {scopeNote && <p className="mj-sub mj-sub--note">{scopeNote}</p>}
        </div>
        <div className="mj-head-actions">
          {/* Where to add everything, with links that open the right screen. */}
          <HowToButton topic={REMINDER_TOPIC[kind] || ""} onNavigate={isModal ? onClose : undefined} />
          {isModal && (
            <button type="button" className="mj-x" onClick={onRemindLater} aria-label="Close and remind me tomorrow">
              ✕
            </button>
          )}
        </div>
      </div>

      {!empty && (
        <>
          <div className="mj-filters">
            <input
              className="mj-search"
              type="search"
              placeholder={kind === "clockedIn" ? "Search name…" : "Search WO, site, city…"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="mj-chips" role="group" aria-label="When">
              {AGE_FILTERS.filter((f) => ageCounts[f.id] > 0 || f.id === "all").map((f) => (
                <button key={f.id} type="button" className={`mj-chip ${age === f.id ? "is-on" : ""}`} onClick={() => setAge(f.id)}>
                  {f.label} <span className="mj-chip-n">{ageCounts[f.id]}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mj-bar">
            <label className="mj-selall">
              <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} />
              <span>
                {allVisibleSelected ? "Unselect" : "Select"} all {visibleRows.length}
              </span>
            </label>
            <span className="mj-selcount">{selected.size ? `${selected.size} selected` : "Select rows, then pick an action"}</span>
            <div className="mj-actions">
              {(kind === "noTech" || kind === "proposalJob") && (
                <button type="button" className={`mj-btn mj-btn--reschedule ${step === "assign" ? "is-on" : ""}`} disabled={!selected.size || Boolean(busy)} onClick={() => openStep("assign")}>
                  <span aria-hidden="true">👷</span> Assign techs
                </button>
              )}
              <button type="button" className={`mj-btn mj-btn--skip ${step === "dismiss" ? "is-on" : ""}`} disabled={!selected.size || Boolean(busy)} onClick={() => openStep("dismiss")}>
                <span aria-hidden="true">🚫</span> Dismiss
              </button>
              {isModal && (
                <button type="button" className="mj-btn mj-btn--later" disabled={!selected.size || Boolean(busy)} onClick={snoozeSelected} title="Hide these until tomorrow">
                  <span aria-hidden="true">⏰</span> Later
                </button>
              )}
            </div>
          </div>

          {step && (
            <div className="mj-step">
              <div className="mj-step-head">
                <strong>{step === "assign" ? "👷 Assign techs" : "🚫 Dismiss for good"}</strong>
                <button type="button" className="mj-link" onClick={() => setStep(null)} disabled={Boolean(busy)}>
                  Cancel
                </button>
              </div>
              <p className="mj-step-text">
                {step === "assign"
                  ? `Add these people to Tech Assign on ${selectedRows.length} job${selectedRows.length === 1 ? "" : "s"}. Recurring jobs get them on the whole series.`
                  : `Dismissed reminders never come back for ${selectedRows.length === 1 ? "this one" : `these ${selectedRows.length}`}. You can undo from Job History → Reminders → Dismissed.`}
              </p>

              {step === "assign" ? (
                <div className="mj-step-grid">
                  <label className="mj-field mj-field--wide">
                    <span>Techs</span>
                    <select
                      value=""
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) setPickNames((p) => (p.includes(v) ? p : [...p, v]));
                      }}
                    >
                      <option value="">Add a tech…</option>
                      {staffNames.filter((n) => !pickNames.includes(n)).map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    <div className="jr-picked">
                      {pickNames.map((n) => (
                        <button key={n} type="button" className="jr-pill" onClick={() => setPickNames((p) => p.filter((x) => x !== n))}>
                          {n} ✕
                        </button>
                      ))}
                    </div>
                  </label>
                </div>
              ) : (
                <div className="mj-step-grid">
                  <label className="mj-field">
                    <span>Why doesn't it need fixing?</span>
                    <select value={reason} onChange={(e) => setReason(e.target.value)}>
                      <option value="">Choose a reason…</option>
                      {def.dismissReasons.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="mj-field mj-field--wide">
                    <span>Note {reason === "Other" ? "" : "(optional)"}</span>
                    <input type="text" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="Anything the next person should know" />
                  </label>
                </div>
              )}

              <div className="mj-step-foot">
                {stepProblem ? <span className="mj-problem">{stepProblem}</span> : <span />}
                <button type="button" className="mj-go" disabled={Boolean(stepProblem) || Boolean(busy)} onClick={runStep}>
                  {busy ? `Saving ${busy.done} of ${busy.total}…` : `${step === "assign" ? "Assign" : "Dismiss"} ${selectedRows.length}`}
                </button>
              </div>
            </div>
          )}

          {message && <div className={`mj-msg mj-msg--${message.kind}`}>{message.text}</div>}

          <ul className="mj-list">
            {visibleRows.map((r) => {
              const isSel = selected.has(r.key);
              return (
                <li key={r.key} className={`mj-row jr-row ${isSel ? "is-sel" : ""}`}>
                  <label className="mj-check">
                    <input type="checkbox" checked={isSel} onChange={() => toggleRow(r.key)} aria-label={`Select ${r.label}`} />
                  </label>

                  {kind === "clockedIn" ? (
                    <div className="mj-main">
                      <div className="mj-line1">
                        <span className="mj-site">{r.name}</span>
                        {r.stillIn && <span className="mj-tag">Still clocked in</span>}
                      </div>
                      <div className="mj-line2">
                        <span>{r.shifts.map((s) => `${fmtTime(s.inAt)}–${s.outAt ? fmtTime(s.outAt) : "now"}`).join(", ")}</span>
                        {r.minutes > 0 && <span>{fmtHours(r.minutes)}</span>}
                      </div>
                      <div className="jr-assign">
                        {r.options.length ? (
                          <>
                            <select value={rowPick[r.key] ?? r.suggestedJobId} onChange={(e) => setRowPick((p) => ({ ...p, [r.key]: e.target.value }))}>
                              <option value="">Pick the job they worked…</option>
                              {r.options.map((o) => (
                                <option key={o.id} value={o.id}>
                                  {o.checkedIn ? "✓ checked in · " : ""}
                                  {o.label}
                                  {o.recurring ? " (this visit only)" : ""}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="mj-btn mj-btn--reschedule"
                              disabled={!(rowPick[r.key] ?? r.suggestedJobId) || Boolean(busy)}
                              onClick={() => assignClockIn(r)}
                            >
                              Assign
                            </button>
                          </>
                        ) : (
                          <span className="mj-history">No jobs were on the calendar that day. Dismiss it, or put a job on that day first.</span>
                        )}
                      </div>
                    </div>
                  ) : kind === "dupWO" ? (
                    <div className="mj-main">
                      <div className="mj-line1">
                        <span className="mj-wo">WO {r.wo}</span>
                        <span className="mj-site">used at {r.sites.length} properties</span>
                      </div>
                      <div className="jr-dup-sites">
                        {r.sites.map((site) => {
                          const openKey = `${r.key}|${site.siteName}`;
                          return (
                            <div key={site.siteName} className="jr-dup-site">
                              <div>
                                <b>{site.siteName}</b>
                                <span className="mj-history">
                                  {" "}
                                  · {site.records.length} record(s): {site.kinds.map((k) => KIND_WORD[k] || k).join(", ")}
                                  {site.scope ? ` · ${formattedToOneLine(site.scope, 60)}` : ""}
                                  {site.latest ? ` · ${fmtShort(site.latest)}` : ""}
                                </span>
                              </div>
                              {dupOpen === openKey ? (
                                <DupWOFix
                                  row={r}
                                  site={site}
                                  onCancel={() => setDupOpen("")}
                                  onDone={(text) => {
                                    setDupOpen("");
                                    setMessage({ kind: "ok", text });
                                    onRenumbered && onRenumbered();
                                  }}
                                />
                              ) : (
                                <button type="button" className="mj-btn mj-btn--reschedule" onClick={() => setDupOpen(openKey)}>
                                  Give {site.siteName} a new WO
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="mj-main" onClick={() => toggleRow(r.key)}>
                      <div className="mj-line1">
                        {r.wo && <span className="mj-wo">WO {r.wo}</span>}
                        <span className="mj-site">{r.siteName || "No site name"}</span>
                        {r.city && <span className="mj-city">{r.city}</span>}
                      </div>
                      <div className="mj-line2">
                        {r.serviceType && <span>{r.serviceType}</span>}
                        {r.status && <span>{r.status}</span>}
                        {kind === "subPay" && <span className="mj-tag jr-tag-sub">Sub: {r.subName}</span>}
                        {kind === "proposalJob" && r.scope && <span>{formattedToOneLine(r.scope, 80)}</span>}
                        {kind === "proposalJob" && r.fromDeposit && <span className="mj-tag">Deposit paid</span>}
                        {kind === "proposalJob" &&
                          (setupDone.has(r.key) ? (
                            <span className="mj-tag jr-tag-done">Scheduled ✓</span>
                          ) : (
                            r.missing.map((m) => (
                              <span key={m} className="mj-tag jr-tag-missing">
                                Needs {m}
                              </span>
                            ))
                          ))}
                        {r.recurring && <span className="mj-tag mj-tag--rec">Recurring</span>}
                      </div>
                    </div>
                  )}

                  <div className="mj-when">
                    <div className="mj-date" title={fmtDay(r.day)}>
                      {fmtShort(r.day)}
                      {r.endDay ? ` – ${fmtShort(r.endDay)}` : ""}
                    </div>
                    {isModal && snoozedSet.has(r.key) && <div className="mj-late">Later</div>}
                  </div>

                  <div className="mj-rowbtns">
                    {kind === "proposalJob" && !setupDone.has(r.key) && (
                      <button type="button" className="mj-link" onClick={() => setSetupOpen((k) => (k === r.key ? "" : r.key))}>
                        {setupOpen === r.key ? "Close" : "Set date & techs"}
                      </button>
                    )}
                    {kind === "subPay" && (
                      <button type="button" className="mj-link" onClick={() => setPayOpen((k) => (k === r.key ? "" : r.key))}>
                        {payOpen === r.key ? "Close pay" : "Set pay"}
                      </button>
                    )}
                    {onOpenJob && r.job && (
                      <button type="button" className="mj-link" onClick={() => onOpenJob(r)}>
                        Open
                      </button>
                    )}
                  </div>

                  {kind === "proposalJob" && setupOpen === r.key && (
                    <ProposalJobSetup
                      row={r}
                      staffNames={staffNames}
                      staffName={staffName}
                      onCancel={() => setSetupOpen("")}
                      onSaved={(patch) => {
                        setSetupOpen("");
                        setSetupDone((prev) => new Set(prev).add(r.key));
                        setMessage({ kind: "ok", text: `${r.label} scheduled for ${fmtShort(patch.startDate)}.` });
                        if (onJobPatch) onJobPatch(r.jobId, patch);
                      }}
                    />
                  )}

                  {kind === "subPay" && payOpen === r.key && (
                    <div className="jr-paypanel">
                      <SubPayPanel
                        key={`rem-${r.jobId}`}
                        job={r.job}
                        userRole={userRole}
                        defaultOpen
                        onJobUpdated={(patch) => onJobPatch && onJobPatch(r.jobId, patch)}
                      />
                    </div>
                  )}
                </li>
              );
            })}
            {!visibleRows.length && <li className="mj-empty">Nothing matches these filters.</li>}
          </ul>

          {isModal && snoozedCount > 0 && (
            <button type="button" className="mj-link mj-snoozed" onClick={() => setShowSnoozed((v) => !v)}>
              {showSnoozed ? "Hide" : "Show"} {snoozedCount} set for later
            </button>
          )}
        </>
      )}

      {isModal && (
        <div className="mj-foot">
          <span className="mj-foot-note">
            {rows.length ? "Closing brings this back tomorrow. The banner on Home opens it anytime. Job History → Reminders has every job." : ""}
          </span>
          <div className="mj-foot-btns">
            {rows.length > 0 ? (
              <button type="button" className="mj-btn mj-btn--later" onClick={onRemindLater} disabled={Boolean(busy)}>
                ⏰ Remind me tomorrow
              </button>
            ) : (
              <button type="button" className="mj-btn" onClick={onClose}>
                Close
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (!isModal) return body;
  return (
    <div className="mj-overlay" onClick={busy ? undefined : onRemindLater}>
      {body}
    </div>
  );
};

/* Banner on the staff Home - one per kind, shown while there's something to fix. */
export const JobReminderBanner = ({ kind, count = 0, onOpen }) => {
  const def = REMINDER_KINDS[kind];
  if (!count || !def) return null;
  return (
    <button type="button" className={`mj-banner jr-banner jr-banner--${kind}`} onClick={onOpen}>
      <span className="mj-banner-icon" aria-hidden="true">
        {def.icon}
      </span>
      <span className="mj-banner-text">
        <strong>{def.banner(count)}</strong>
      </span>
      <span className="mj-banner-cta">Review</span>
    </button>
  );
};

export default JobRemindersReview;
